/********************************************************* {COPYRIGHT-TOP} ***
 * Licensed Materials - Property of IBM
 *
 * (C) Copyright IBM Corporation 2021, 2024
 *
 * All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 ********************************************************** {COPYRIGHT-END} **/
import React, { useRef } from 'react';
import { useToggle } from 'hooks/booleanHooks';
import { useTranslation } from 'react-i18next';
import { Loading } from '@carbon/react';
import { getHighlightClassForDependencies } from 'utils/CodeViewerLanguageMapper';
import ExpandableDisplay from './ExpandableDisplay';
import { CodeViewer } from './CodeViewer';
import { uniqueId } from 'lodash';
import classNames from 'classnames';
import PropTypes from 'prop-types';

const Dependencies = ({ generating, dependencyContent = '', errorText, activeCodeLanguage = '', label }) => {
  const { t } = useTranslation();
  const [expanded, toggleExpanded] = useToggle(false);
  const copyRef = useRef(`code-snippet-${uniqueId()}`);
  const copyId = copyRef.current;

  const renderError = (errorText) => {
    return <div data-testid={'snippet-error'}>{t('generator_error', { error: errorText })}</div>;
  };

  const renderLoading = () => {
    return <Loading small withOverlay={false} data-testid={'loading-icon'} />;
  };

  return (
    <div>
      {errorText ? (
        renderError(errorText)
      ) : generating ? (
        renderLoading()
      ) : (
        <div>
          {label && (
            <p role="heading" aria-level={4} className="codeSnippetLabel" data-testid={'dependencies-label'}>
              {label}
            </p>
          )}
          <ExpandableDisplay expanded={expanded} onExpand={toggleExpanded}>
            <CodeViewer
              data-copytarget={copyId}
              data-testid={'generated-dependencies'}
              codeLanguage={getHighlightClassForDependencies(activeCodeLanguage)}
              className={classNames('codeSnippetContent', { expanded: expanded })}
            >
              {dependencyContent}
            </CodeViewer>
          </ExpandableDisplay>
        </div>
      )}
    </div>
  );
};

Dependencies.propTypes = {
  generating: PropTypes.bool,
  dependencyContent: PropTypes.string,
  errorText: PropTypes.string,
  activeCodeLanguage: PropTypes.string,
  label: PropTypes.string
};

export default Dependencies;
